// Hook para gerenciar autenticação no frontend
import { useState, useEffect, createContext, useContext } from 'react';

// Context de autenticação
const AuthContext = createContext(null);

// Configuração da API
const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://dashsimples.vercel.app/api' // Sempre usar Vercel para APIs
  : 'http://localhost:3001/api';

// Provider de autenticação
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verificar token no localStorage ao carregar
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Verificar status de autenticação
  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.data.user);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('auth_token');
        }
      } else {
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('❌ Auth check error:', error);
      localStorage.removeItem('auth_token');
    } finally {
      setLoading(false);
    }
  };

  // Função de login
  const login = async (email, password) => {
    try {
      console.log('🔐 Tentativa de login para:', email);
      
      // Primeiro, obter token CSRF
      const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include'
      });
      
      let csrfToken = '';
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken || '';
      }
      
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      console.log('📝 Resposta do login:', { status: response.status, data });

      if (response.ok && data.success) {
        localStorage.setItem('auth_token', data.data.token);
        setUser(data.data.user);
        setIsAuthenticated(true);
        return { success: true, user: data.data.user };
      } else {
        // Para erros específicos como EMAIL_NOT_VERIFIED, lançar erro com dados completos
        const errorDetails = { 
          message: data.error || 'Erro no login',
          code: data.code,
          data: data.data,
          details: data.details,
          canResendCode: data.canResendCode
        };
        
        console.log('❌ Erro de login:', errorDetails);
        
        // Lançar erro para ser capturado no catch do componente
        throw errorDetails;
      }
    } catch (error) {
      console.error('❌ Login error completo:', error);
      
      // Se já é um erro estruturado, relançar
      if (error.code || error.message) {
        throw error;
      }
      
      // Caso contrário, erro de conexão
      throw { 
        message: 'Erro de conexão. Tente novamente.',
        code: 'CONNECTION_ERROR'
      };
    }
  };

  // Função de registro
  const register = async (email, password, confirmPassword, name) => {
    try {
      // Primeiro, obter token CSRF
      const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include'
      });
      
      let csrfToken = '';
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken || '';
      }

      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ 
          email, 
          password, 
          confirmPassword, 
          name 
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Não logar automaticamente - requer verificação de email
        return { 
          success: true, 
          user: data.data.user,
          requiresVerification: data.data.requiresVerification || false,
          message: data.message
        };
      } else {
        return { 
          success: false, 
          error: data.error || 'Erro no registro',
          details: data.details
        };
      }
    } catch (error) {
      console.error('❌ Register error:', error);
      return { 
        success: false, 
        error: 'Erro de conexão. Tente novamente.' 
      };
    }
  };

  // Função para verificar email
  const verifyEmail = async (email, code) => {
    try {
      // Logs apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Iniciando verifyEmail:', { email, code });
      }
      
      // Primeiro, obter token CSRF
      const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include'
      });
      
      let csrfToken = '';
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken || '';
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ CSRF token obtido:', csrfToken.substring(0, 20) + '...');
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ Erro ao obter CSRF token:', csrfResponse.status);
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('📡 Fazendo requisição verify-email...');
      }
      
      const response = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ email, code })
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Resposta recebida:', { status: response.status, ok: response.ok });
      }
      
      const data = await response.json();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📋 Dados da resposta:', data);
      }

      if (response.ok && data.success) {
        // Armazenar token e usuário no localStorage se recebidos
        if (data.data?.token) {
          localStorage.setItem('auth_token', data.data.token); // CORRIGIDO: usar 'auth_token'
          localStorage.setItem('user', JSON.stringify(data.data.user));
          setUser(data.data.user);
          setIsAuthenticated(true);
        }

        return { 
          success: true, 
          message: data.message,
          user: data.data.user
        };
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ Verify email failed:', { status: response.status, data });
        }
        return { 
          success: false, 
          error: data.error || 'Erro na verificação'
        };
      }
    } catch (error) {
      console.error('❌ Verify email error:', error);
      return { 
        success: false, 
        error: 'Erro de conexão. Tente novamente.' 
      };
    }
  };

  // Função para reenviar código de verificação
  const resendVerificationCode = async (email) => {
    try {
      // Primeiro, obter token CSRF
      const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
        method: 'GET',
        credentials: 'include'
      });
      
      let csrfToken = '';
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        csrfToken = csrfData.csrfToken || '';
      }

      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        return { 
          success: true, 
          message: data.message
        };
      } else {
        return { 
          success: false, 
          error: data.error || 'Erro ao reenviar código'
        };
      }
    } catch (error) {
      console.error('❌ Resend code error:', error);
      return { 
        success: false, 
        error: 'Erro de conexão. Tente novamente.' 
      };
    }
  };

  // Função de logout
  const logout = async (logoutAll = false) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Primeiro, obter token CSRF
        const csrfResponse = await fetch(`${API_URL}/csrf-token`, {
          method: 'GET',
          credentials: 'include'
        });
        
        let csrfToken = '';
        if (csrfResponse.ok) {
          const csrfData = await csrfResponse.json();
          csrfToken = csrfData.csrfToken || '';
        }

        const endpoint = logoutAll ? '/auth/logout-all' : '/auth/logout';
        await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          }
        });
      }
    } catch (error) {
      console.error('❌ Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Função para obter token
  const getToken = () => {
    return localStorage.getItem('auth_token');
  };

  // Função para fazer requisições autenticadas
  const authenticatedFetch = async (url, options = {}) => {
    const token = getToken();
    if (!token) {
      throw new Error('Token de acesso não encontrado');
    }

    const config = {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    };

    const response = await fetch(url, config);

    // Se token expirou, fazer logout
    if (response.status === 401) {
      await logout();
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    return response;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    verifyEmail,
    resendVerificationCode,
    logout,
    getToken,
    authenticatedFetch,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook para usar o contexto de autenticação
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

// Hook para verificar se usuário está autenticado
export function useRequireAuth() {
  const { isAuthenticated, loading } = useAuth();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Aqui você pode redirecionar para login ou mostrar modal
      console.log('Usuário não autenticado');
    }
  }, [isAuthenticated, loading]);

  return { isAuthenticated, loading };
}

export default AuthContext;
